import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';

// Tone palettes — kept in sync with the v2 colour language (styles/theme.js)
const TONES = {
	info: { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af', dashed: false },
	warning: { bg: '#fbf0dd', border: '#f0d9a8', color: '#8a5000', dashed: false },
	neutral: { bg: '#fafafa', border: '#d9d9d9', color: '#595959', dashed: true },
};

const Banner = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 8px;
	background: ${(props) => TONES[props.tone].bg};
	border: 1px ${(props) => (TONES[props.tone].dashed ? 'dashed' : 'solid')}
		${(props) => TONES[props.tone].border};
	border-radius: 6px;
	padding: 10px 14px;
	margin-bottom: 12px;
	font-size: 12px;
	line-height: 1.5;
	color: ${(props) => TONES[props.tone].color};

	.anticon {
		margin-top: 2px;
		flex-shrink: 0;
	}
`;

/**
 * Inline contextual callout used across the v2 forms and the warehousing module.
 *
 * tone="info"     — blue, explanatory
 * tone="warning"  — amber, needs attention but not an error
 * tone="neutral"  — grey dashed, passive reference note
 */
export default function InfoBanner({ tone, icon, children, className }) {
	const showIcon = icon !== false;
	const IconComponent = tone === 'warning' ? WarningOutlined : InfoCircleOutlined;

	return (
		<Banner tone={tone} className={className}>
			{showIcon && (icon || <IconComponent />)}
			<span>{children}</span>
		</Banner>
	);
}

InfoBanner.propTypes = {
	tone: PropTypes.oneOf(['info', 'warning', 'neutral']),
	// `false` hides the icon entirely, a node overrides the default one
	icon: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};

InfoBanner.defaultProps = {
	tone: 'info',
	icon: undefined,
	className: undefined,
};
